import {
  IconHeaderWithSearch,
  LightSplitLayout,
  LightSplitLayoutProps,
  StringUtils,
  useLocalStorage,
  ExportUtils,
  ConfigObjectType,
  useCurrentLocale,
  NoDataMessageWithIcon,
} from '@wallaby-ui/core';
import {
  AG_GRID_COMPATIBILITY,
  ChartColor,
  DEFAULT_PAGE_SIZE,
  Formatters,
  HaidiSchema,
  JustTooltipFormatter,
  LightChart,
  LightChartLayout,
  LightChartLegend2,
  LightSelectedFilters,
  MaterialTableWrapper,
  ProgressBarFormatter,
  TableSorting,
  getBarChartHeight,
  getBarChartItems,
  getDefaultMTOptions,
  getTableMaxHeight,
} from '@wallaby-ui/document';
import {get, sortBy} from 'lodash';
import {Column} from 'material-table';
import React, {ReactNode, useCallback, useMemo} from 'react';

import {useStandardTypes} from '../encounter/hai/useStandardTypes';
import {filterLegendByHaiState, markChartDataLegendByHaiState} from './reportLegendUtils';
import {useChartHiddenMap} from '../../common/useChartHiddenMap';

export type HospitalReportData = HaidiSchema.components['schemas']['HaiDepartmentsDataDto'];
type RowType = HaidiSchema.components['schemas']['HaiDepartmentStatisticsDto'];
type RowTypeWithTotal = RowType & {isTotalRow?: boolean};
function useColumns(
  departmentColumnLabel: string,
  getReportLink: ((data: RowType) => string | undefined) | undefined,
  formatters: Formatters,
  defaultSort: TableSorting
): Column<RowTypeWithTotal>[] {
  const EMPTY = '-';
  const {t, formatFloat} = formatters;
  // const xlOrLarger = useXlOrLarger();
  const columns: Column<RowTypeWithTotal>[] = [
    {
      field: 'departmentName',
      title: t(departmentColumnLabel),
      render: (data) => {
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={data.departmentName || EMPTY}
            tooltipOverride={data.departmentName}
            bold={data.isTotalRow}
            route={getReportLink && getReportLink(data)}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'statistics.haiCount',
      title: t('reports.haiCountPerDepartment.haiCount'),
      render: (data) => {
        const haiCountString = formatFloat(data.statistics.haiCount);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={haiCountString ?? EMPTY}
            tooltipOverride={haiCountString}
            bold={data.isTotalRow}
          />
        );
      },
      width: '122px',
      sorting: true,
    },
    {
      field: 'statistics.hospWithHaiPercentage',
      title: t('reports.haiCountPerDepartment.hospWithHaiPercentage'),
      render: (data) => {
        const percent = data.statistics.hospWithHaiPercentage;
        const percentString = `${formatFloat(percent)} %`;
        return (
          <ProgressBarFormatter
            {...AG_GRID_COMPATIBILITY}
            value={percentString ?? EMPTY}
            tooltipOverride={percentString}
            bold={data.isTotalRow}
            progressPercent={percent}
            style={{maxWidth: '150px'}}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'statistics.hospCount',
      title: t('reports.haiCountPerDepartment.hospCount'),
      render: (data) => {
        const hospCountString = formatFloat(data.statistics.hospCount);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={hospCountString ?? EMPTY}
            tooltipOverride={hospCountString}
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'statistics.careDaysCount',
      title: t('reports.haiCountPerDepartment.careDaysCount'),
      render: (data) => {
        const careDaysCountString = formatFloat(data.statistics.careDaysCount);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={careDaysCountString ?? EMPTY}
            tooltipOverride={careDaysCountString}
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'statistics.haiCountPer1000CareDays',
      title: t('reports.haiCountPerDepartment.haiCountPer1000CareDays'),
      render: (data) => {
        const haiCountPer1000CareDaysString = formatFloat(data.statistics.haiCountPer1000CareDays);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={haiCountPer1000CareDaysString ?? EMPTY}
            tooltipOverride={haiCountPer1000CareDaysString}
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
    {
      field: 'statistics.deathDueToHaiCount',
      title: t('reports.haiCountPerDepartment.deathDueToHaiCount'),
      render: (data) => {
        const deathDueToHaiCountString = formatFloat(data.statistics.deathDueToHaiCount);
        return (
          <JustTooltipFormatter
            {...AG_GRID_COMPATIBILITY}
            value={deathDueToHaiCountString ?? EMPTY}
            tooltipOverride={deathDueToHaiCountString}
            bold={data.isTotalRow}
          />
        );
      },
      sorting: true,
    },
  ];
  return columns.map((v) => ({
    ...v,
    sorting: v.sorting ?? false,
    customSort: (row1, row2, _type) => {
      const isSortAscending = defaultSort.direction === 'asc';
      if (row1.isTotalRow) return isSortAscending ? -1 : 1;
      if (row2.isTotalRow) return isSortAscending ? 1 : -1;
      const field = v.field ?? '';
      const data1 = get(row1, field);
      const data2 = get(row2, field);
      return +(data1 > data2) - +(data1 < data2);
    },
    ...(v.field === defaultSort.field ? {defaultSort: defaultSort.direction ?? undefined} : {}),
  }));
}
type Props = {
  departmentTableAndChartLabel: string;
  departmentColumnLabel: string;
  getReportLink?: (data: RowTypeWithTotal) => string | undefined;
  muiStyles: any;
  storageIdPrefix: string;
  filters: any;
  changeFilters: (newFilters: any) => void;
  hasSorting: boolean;
  handleResetSorting: () => void;
  hasFilters: boolean;
  handleResetFilters: () => void;
  formatters: Formatters;
  isLoading: boolean;
  reportData: HospitalReportData | undefined;
  config: ConfigObjectType;
  reportName: string;
};
export const HospitalReportTableAndCharts: React.FC<Props> = (props) => {
  const {
    departmentTableAndChartLabel,
    departmentColumnLabel,
    getReportLink,
    muiStyles,
    storageIdPrefix,
    filters,
    changeFilters,
    hasSorting,
    handleResetSorting,
    hasFilters,
    handleResetFilters,
    formatters,
    isLoading,
    reportData,
    config,
    reportName,
  } = props;
  const {t, tEnum} = formatters;
  const columns = useColumns(departmentColumnLabel, getReportLink, formatters, filters.reportSorting);
  // table data
  const rawDepartmentsData = reportData?.departments ?? [];
  const departmentsData: RowTypeWithTotal[] = [
    {
      departmentName: t('reports.totalRow'),
      haiCounts: {} as any,
      statistics: {
        haiCount: reportData?.totalDataStatistics?.statistics?.haiCount ?? 0,
        hospWithHaiPercentage: reportData?.totalDataStatistics?.statistics?.hospWithHaiPercentage ?? 0,
        hospCount: reportData?.totalDataStatistics?.statistics?.hospCount ?? 0,
        careDaysCount: reportData?.totalDataStatistics?.statistics?.careDaysCount ?? 0,
        haiCountPer1000CareDays: reportData?.totalDataStatistics?.statistics?.haiCountPer1000CareDays ?? 0,
        deathDueToHaiCount: reportData?.totalDataStatistics?.statistics?.deathDueToHaiCount ?? 0,
      },
      isTotalRow: true,
    },
    ...rawDepartmentsData,
  ];
  const totalCount = departmentsData.length;
  // charts
  const splitHorizontal: LightSplitLayoutProps = {
    initSizes: [50, 50],
    minSizes: [100, 100],
    storageId: `${storageIdPrefix}.splitHorizontal`,
    isVertical: false,
  };
  const baseChartColors: ChartColor[] = [
    {
      id: 'YES',
      color: '#FF8717',
      label: tEnum('State', 'YES'),
    },
    {
      id: 'COL',
      color: '#6FA9FF',
      label: tEnum('State', 'COL'),
    },
  ];
  // Each chart owns its own hidden-map so toggling one chart's legend doesn't affect the other.
  const byDepartmentHidden = useChartHiddenMap();
  const byTypeHidden = useChartHiddenMap();
  const byDepartmentChartColors = byDepartmentHidden.applyHidden(baseChartColors);
  const byTypeChartColors = byTypeHidden.applyHidden(baseChartColors);
  const byDepartmentLegendItems = filterLegendByHaiState(byDepartmentChartColors, filters.report.haiState);
  const byTypeLegendItems = filterLegendByHaiState(byTypeChartColors, filters.report.haiState);
  // Drop the non-selected HAI state from the downloaded image's legend (keeps it in sync with the
  // on-screen filtered legend); the series themselves stay built from the full color set.
  const byDepartmentDataChartColors = markChartDataLegendByHaiState(byDepartmentChartColors, filters.report.haiState);
  const byTypeDataChartColors = markChartDataLegendByHaiState(byTypeChartColors, filters.report.haiState);
  const chartByDepartmentData = reportData?.statisticsByDepartment ?? [];
  const chartByTypeData = reportData?.statisticsByType ?? [];
  const chartByDepartmentHeight = getBarChartHeight(chartByDepartmentData.length);
  const chartByTypeHeight = getBarChartHeight(chartByTypeData.length);
  const maxChartHeight = Math.max(chartByDepartmentHeight, chartByTypeHeight);
  const CHART_LAYOUT: LightChartLayout = {
    margin: {},
    xaxis: {
      nticks: 10,
      automargin: true,
    },
    yaxis: {
      automargin: true,
      fixedrange: true,
    },
    showlegend: false,
    barmode: 'stack',
    height: maxChartHeight,
  };
  const CHART_ELLIPSIS_MAX_LENGTH = 15;
  const [defaultTableScroll, setDefaultTableScroll] = useLocalStorage(`${storageIdPrefix}.tableScroll`, 0);
  const currentLocale = useCurrentLocale();
  const downloadXlsxExport = useCallback(() => {
    ExportUtils.handleExportXlsx({
      url: config.service.url.haiXlsxExport.replace('{reportName}', reportName),
      filters: filters.report,
      locale: currentLocale,
    });
  }, [config.service.url.haiXlsxExport, currentLocale, filters, reportName]);
  const {standardTypeTranslations} = useStandardTypes({config});
  const getLabel: (v: {label: string}) => string =
    filters.report.typeCategory === 'HAI_TYPE'
      ? (v) => tEnum('HaiTypeEnum', v.label)
      : (() => {
          return (v) => (v.label == null ? t('reports.uncategorized') : `${v.label} - ${standardTypeTranslations[v.label] ?? v.label}`);
        })();

  const noDataComponent = useMemo(() => <NoDataMessageWithIcon message={'chart.emptyData'} isHaidi />, []);

  return (
    <>
      <div data-name='department-list-wrapper' css={muiStyles.contentBox}>
        <IconHeaderWithSearch
          iconName={'listIconMultiColor'}
          label={departmentTableAndChartLabel}
          useFixedViewBox='0 -1 16 14'
          iconCss={muiStyles.headerIcon}
        >
          <LightSelectedFilters
            hasFilters={hasFilters}
            handleResetFilters={handleResetFilters}
            hasSorting={hasSorting}
            handleResetSorting={handleResetSorting}
            hasXlsxExport
            handleXlsxExport={() => downloadXlsxExport()}
          />
        </IconHeaderWithSearch>
        <MaterialTableWrapper
          style={{maxHeight: getTableMaxHeight(15)}}
          defaultScroll={{to: defaultTableScroll}}
          onScroll={(event) => setDefaultTableScroll((event.target as HTMLDivElement).scrollTop)}
          singleLineColumnLabels
          options={{
            ...getDefaultMTOptions(DEFAULT_PAGE_SIZE),
            paging: false,
          }}
          variant='big'
          totalRow='top'
          useMaxHeight
          columns={columns}
          data={departmentsData}
          isLoading={isLoading}
          totalCount={totalCount}
          onOrderChange={(colIndex, direction) => {
            const columnId = columns[colIndex].field as string;
            changeFilters({
              reportSorting: {
                field: columnId,
                direction,
              },
            });
          }}
          controlledSort={(() => {
            return filters.reportSorting;
          })()}
        />
      </div>
      <LightSplitLayout data-name='charts-wrapper' {...splitHorizontal} style={{height: 'auto'}}>
        <div css={muiStyles.contentBox} style={{rowGap: 0}}>
          <IconHeaderWithSearch
            iconName={'barChartIconMultiColor'}
            label={departmentTableAndChartLabel}
            useFixedViewBox='0 0 15 14'
            iconCss={muiStyles.headerIcon}
          >
            <LightChartLegend2
              items={byDepartmentLegendItems}
              hiddenMap={byDepartmentHidden.hiddenMap}
              onSetHiddenMap={byDepartmentHidden.onSetHiddenMap}
            />
          </IconHeaderWithSearch>
          <LightChart
            imageDownload={{
              title: {
                text: t(departmentTableAndChartLabel),
              },
              showlegend: true,
            }}
            data={getBarChartItems(
              sortBy(chartByDepartmentData, (v) => v.label),
              byDepartmentDataChartColors,
              (v, i) => {
                const x = [v.confirmedHaisCount, v.colonizationsCount][i];
                const y = v.label;
                return {
                  x,
                  y: StringUtils.doEllipsis(y, CHART_ELLIPSIS_MAX_LENGTH),
                  yTooltip: y,
                };
              }
            )}
            layout={CHART_LAYOUT}
            noDataComponent={noDataComponent}
          />
        </div>
        <div css={muiStyles.contentBox} style={{rowGap: 0}}>
          <IconHeaderWithSearch
            iconName={'barChartIconMultiColor'}
            label={'reports.haiCountPerType'}
            useFixedViewBox='0 0 15 14'
            iconCss={muiStyles.headerIcon}
          >
            <LightChartLegend2 items={byTypeLegendItems} hiddenMap={byTypeHidden.hiddenMap} onSetHiddenMap={byTypeHidden.onSetHiddenMap} />
          </IconHeaderWithSearch>
          <LightChart
            imageDownload={{
              title: {
                text: t('reports.haiCountPerType'),
              },
              showlegend: true,
            }}
            data={getBarChartItems(
              sortBy(chartByTypeData, (v) => getLabel(v)),
              byTypeDataChartColors,
              (v, i) => {
                const x = [v.confirmedHaisCount, v.colonizationsCount][i];
                const y = getLabel(v);
                return {
                  x,
                  y: StringUtils.doEllipsis(y, CHART_ELLIPSIS_MAX_LENGTH),
                  yTooltip: y,
                };
              }
            )}
            layout={CHART_LAYOUT}
            noDataComponent={noDataComponent}
          />
        </div>
      </LightSplitLayout>
    </>
  );
};
